import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

import { useLanguage } from '../../hooks/useLanguage';
import { Purchase } from '../../store/api/productsApi';
import { formatDate } from '../../utils/dateFormatter';

interface PurchaseCardProps {
  purchase: Purchase;
}

export function PurchaseCard({ purchase }: PurchaseCardProps) {
  const { t } = useTranslation();
  const { currentLanguage } = useLanguage();
  const productValue = purchase.transactionDetails?.productValue;

  const handleCopyValue = async () => {
    if (!productValue) return;
    try {
      await navigator.clipboard.writeText(productValue);
      toast.success(t('products.purchase.copied'));
    } catch {
      toast.error(t('products.purchase.copyError'));
    }
  };

  return (
    <div className="bg-neutral-dark/50 backdrop-blur-sm border border-neutral-dark rounded-lg p-4 hover:border-brand-accent/50 transition-all duration-300">
      <div className="flex flex-col gap-4">
        {/* Header */}
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <h3 className="text-lg font-heading font-semibold text-brand-accent mb-1">
              {purchase.productName}
            </h3>
            <Link
              to={`/catalog/category/${purchase.categoryId}`}
              className="text-sm text-neutral-medium font-body hover:text-brand-accent transition-colors"
            >
              {purchase.categoryName}
            </Link>
          </div>
          <div className="text-right">
            <p className="text-xl font-heading font-bold text-brand-accent">
              {purchase.price.toFixed(2)} USD
            </p>
          </div>
        </div>

        {/* Product Value (when available from API) */}
        {productValue && (
          <div className="p-3 bg-brand-accent/10 rounded-lg border border-brand-accent/30">
            <h4 className="text-sm font-heading font-semibold text-brand-accent mb-2">
              {t('products.purchase.valueTitle')}
            </h4>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
              <p className="flex-1 font-mono text-sm font-medium text-brand-accent whitespace-pre-line break-words min-w-0">
                {productValue}
              </p>
              <button
                type="button"
                onClick={() => void handleCopyValue()}
                className="flex-shrink-0 px-3 py-2 bg-brand-accent text-brand-primary font-heading font-semibold rounded-lg hover:shadow-lg hover:shadow-brand-accent/50 transition-all duration-300 text-sm"
                title={t('products.purchase.copy')}
              >
                {t('products.purchase.copy')}
              </button>
            </div>
          </div>
        )}

        {/* Purchase Date */}
        <div className="pt-4 border-t border-neutral-dark">
          <p className="text-xs text-neutral-medium font-body">
            {t('products.purchases.purchasedAt')}: {formatDate(purchase.purchasedAt, 'PP h:mm a', currentLanguage)}
          </p>
        </div>
      </div>
    </div>
  );
}

